import memberWorkspaceAbilitiesStyles from './member-workspace-abilities.styles.css?inline';
import '../../../components/state-badge/agwa-state-badge.js';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { SecurityApi } from '../../../api/security/security-api';
import { toErrorMessage } from '../../../api/error-utils';
import {
  AttachDomainScopeInput,
  SecurityDomainDto,
  SecurityDomainScopeDto,
} from '../../../api/agwa-api-model';
import { confirmAction } from '../../../components/dialogs/agwa-confirm-dialog';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import {
  delegationDomainNote,
  getOrganizationTypeDescription,
  getOrganizationTypeLabel,
} from '../shared/organization-type-labels';

import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import { terms } from '../../../language/terms';
// The repository's comparison for enum-like strings arriving from an API response.
import { isSame } from '../../../utility/strings/strings-utils';
import { SHOW_UNASSIGNABLE_OPERATION_TYPES } from '../shared/operation-type-visibility';

const SYSTEM_TENANT_DOMAIN_CODE = 'AGWA.SystemTenant';

@customElement('member-workspace-abilities')
export class MemberWorkspaceAbilities extends LitElement {
  static styles = [sharedStyles, unsafeCSS(memberWorkspaceAbilitiesStyles)];

  @property({ type: String }) groupId = '';

  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private domains: SecurityDomainDto[] = [];
  @state() private scopes: SecurityDomainScopeDto[] = [];
  @state() private attachingDomain: string | null = null;
  @state() private statusMessage: string | null = null;
  @state() private statusVariant:
    'primary' | 'success' | 'warning' | 'danger' | 'neutral' = 'neutral';

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('groupId')) {
      if (this.groupId) {
        void this.loadData();
      } else {
        this.resetState();
      }
    }
  }

  private resetState() {
    this.loading = false;
    this.error = null;
    this.domains = [];
    this.scopes = [];
    this.attachingDomain = null;
    this.statusMessage = null;
  }

  private async loadData() {
    if (!this.groupId) {
      this.resetState();
      return;
    }
    this.loading = true;
    this.error = null;
    try {
      const api = ApiFactory.authenticated(SecurityApi);
      const [domainsResp, scopesResp] = await Promise.all([
        api.getSecurityDomains(),
        api.getGroupDomainScopes(this.groupId),
      ]);

      if (!domainsResp.success) {
        throw new Error(toErrorMessage(domainsResp));
      }
      if (!scopesResp.success) {
        throw new Error(toErrorMessage(scopesResp));
      }

      this.domains = domainsResp.data?.domains ?? [];
      this.scopes = scopesResp.data?.scopes ?? [];
    } catch (error) {
      this.error =
        (error as Error)?.message ??
        `Unable to load ${terms.userGroupDomain.lower} information.`;
    } finally {
      this.loading = false;
    }
  }

  private get attachedDomainCodes() {
    return new Set(this.scopes.map((scope) => scope.domainCode));
  }

  private get availableDomains() {
    const attached = this.attachedDomainCodes;
    return this.domains.filter((domain) => {
      if (!domain.code) return false;
      if (domain.code === SYSTEM_TENANT_DOMAIN_CODE) return false;
      // Only show Group-level domains at the member level
      // Site-level domains (like Prime.Glass.Manufacturer) are assigned to sites, not members
      if (domain.scopeLevel !== 'Group') return false;
      return !attached.has(domain.code);
    });
  }

  private async handleRegister(domainCode: string) {
    if (!this.groupId || !domainCode) {
      return;
    }
    // Delegation / internal domains are not self-service assignable (server enforces this too).
    const domain = this.domains.find((d) => d.code === domainCode);
    if (domain?.uiAssignable === false) {
      return;
    }
    const friendlyLabel = getOrganizationTypeLabel(domainCode);

    // WHICH DOMAINS CREATE SOMETHING, asked of the catalogue rather than of a hard-coded code.
    // Attaching a domain that OWNS data in a module also creates that module's instance row - a
    // register for a Prime System Supplier, a processor record for a Prime Glass Manufacturer - and
    // that is the part a person needs warning about, because it is what cannot be undone.
    //
    // This replaced `domainCode === 'Product.Registry'`, which had been dead since D-067 renamed
    // that domain: the specific warning had stopped appearing for the domain it was written for,
    // and every reader got the generic "cannot be undone" on an irreversible action. It was also
    // wrong in the other direction from the day it was written - Prime.Glass.Manufacturer owns
    // Glass.Accreditation and creates an instance too, and never matched the comparison.
    //
    // `Owner` and not merely "has an ownership": Contributor and Viewer grant reach into a module
    // that already exists and create nothing, so warning on them would cry wolf.
    const createsAnInstance = (domain?.moduleOwnerships ?? []).some(
      (ownership) => isSame(ownership.ownershipType, 'Owner')
    );
    const message = createsAnInstance
      ? `Make this ${terms.loginGroup.lower} a ${friendlyLabel}? This creates the records that ${terms.userGroupDomain.lower} owns and cannot be undone.`
      : `Make this ${terms.loginGroup.lower} a ${friendlyLabel}? This action cannot be undone.`;
    const confirmed = await confirmAction({
      title: `Confirm ${terms.userGroupDomain.lower}`,
      message,
      confirmText: `Yes, make them ${friendlyLabel}`,
      cancelText: 'Cancel',
      variant: 'warning',
    });
    if (!confirmed) {
      return;
    }
    this.attachingDomain = domainCode;
    this.statusMessage = null;
    try {
      const payload: AttachDomainScopeInput = {
        loginGroupId: this.groupId,
        domainCode,
        autoGrantDefaults: true,
        active: true,
      };
      const api = ApiFactory.authenticated(SecurityApi);
      const response = await api.attachDomainScope(domainCode, payload);
      if (!response.success) {
        throw new Error(toErrorMessage(response));
      }
      await this.loadData();
      this.statusMessage = `${friendlyLabel} ${terms.userGroupDomain.lower} registered successfully.`;
      this.statusVariant = 'success';
      this.dispatchEvent(
        new CustomEvent('abilities-updated', {
          bubbles: true,
          composed: true,
        })
      );
    } catch (error) {
      this.statusMessage =
        (error as Error)?.message ??
        `Unable to register ${terms.userGroupDomain.lower}.`;
      this.statusVariant = 'danger';
    } finally {
      this.attachingDomain = null;
    }
  }

  private renderStatus() {
    if (!this.statusMessage) {
      return nothing;
    }
    return html`<sl-alert variant=${this.statusVariant} open closable>
      ${this.statusMessage}
    </sl-alert>`;
  }

  private renderCurrentOrganizationTypes() {
    if (this.loading && this.scopes.length === 0) {
      return html`<div class="empty-state">
        <sl-spinner></sl-spinner>
        <span>Loading ${terms.userGroupDomain.lowerPlural}…</span>
      </div>`;
    }
    if (!this.scopes.length) {
      return html`<sl-card>
        <div class="domain-meta">
          <strong
            >No ${terms.userGroupDomain.lowerPlural} registered yet.</strong
          >
          <span class="muted"
            >Use the Available ${terms.userGroupDomain.plural} section below to
            register this ${terms.loginGroup.lower}.</span
          >
        </div>
      </sl-card>`;
    }
    return html`<table class="organization-types">
      <thead>
        <tr>
          <th scope="col">This ${terms.loginGroup.lower} is a</th>
          <th scope="col">Active</th>
        </tr>
      </thead>
      <tbody>
        ${this.scopes.map((scope) => this.renderOrganizationTypeRow(scope))}
      </tbody>
    </table>`;
  }

  private renderRegisterSection() {
    if (!this.groupId) {
      return nothing;
    }
    const available = this.availableDomains;
    const assignable = available.filter((d) => d.uiAssignable !== false);
    // Empty unless the switch is on, which keeps the renderer below intact and makes the whole
    // behaviour one constant to flip. See operation-type-visibility.ts for why it is off.
    const unavailable = SHOW_UNASSIGNABLE_OPERATION_TYPES
      ? available.filter((d) => d.uiAssignable === false)
      : [];
    return html`<div class="section">
      <div class="section-header">
        <h3>Available ${terms.userGroupDomain.plural}</h3>
        <p class="muted">
          Add this ${terms.loginGroup.lower} to another
          ${terms.userGroupDomain.lower}. This action is permanent.
        </p>
      </div>
      ${
        assignable.length === 0
          ? html`<sl-alert variant="neutral" open>
              All available ${terms.userGroupDomain.lowerPlural} are already
              registered.
            </sl-alert>`
          : html`<div class="available-list">
              ${assignable.map((domain) =>
                this.renderAvailableOrganizationType(domain)
              )}
            </div>`
      }
      ${
        unavailable.length > 0
          ? html`<sl-divider></sl-divider>
              <div class="section-header">
                <h3>Unavailable ${terms.userGroupDomain.plural}</h3>
                <p class="muted">
                  Managed by ${terms.agwa.value} and not available to assign
                  here.
                </p>
              </div>
              <div class="available-list">
                ${unavailable.map((domain) =>
                  this.renderAvailableOrganizationType(domain)
                )}
              </div>`
          : nothing
      }
    </div>`;
  }

  private renderOrganizationTypeRow(
    scope: SecurityDomainScopeDto
  ): TemplateResult {
    const label = getOrganizationTypeLabel(scope.domainCode);
    return html`<tr>
      <td>${label}</td>
      <td>
        <agwa-state-badge
          domain="memberDomainScope"
          state=${scope.active ? 'Active' : 'Inactive'}
        ></agwa-state-badge>
      </td>
    </tr>`;
  }

  private renderAvailableOrganizationType(
    domain: SecurityDomainDto
  ): TemplateResult {
    const label = getOrganizationTypeLabel(domain.code);
    const description = getOrganizationTypeDescription(domain.code);
    const disabled = domain.uiAssignable === false;
    const buttonText = `Make this ${terms.loginGroup.lower} a ${label}`;
    return html`<div class="available-item">
      <div>
        <div class="available-label">${label}</div>
        ${
          description
            ? html`<div class="available-description">${description}</div>`
            : nothing
        }
        ${
          disabled
            ? html`<div class="delegation-note">${delegationDomainNote}</div>`
            : nothing
        }
      </div>
      <sl-button
        variant="primary"
        size="small"
        ?disabled=${disabled}
        ?loading=${this.attachingDomain === domain.code}
        @click=${() => this.handleRegister(domain.code)}
      >
        ${buttonText}
      </sl-button>
    </div>`;
  }

  render() {
    if (!this.groupId) {
      return html`<sl-alert variant="neutral" open>
        Select a ${terms.loginGroup.lower} to manage abilities.
      </sl-alert>`;
    }
    return html`<div class="abilities-root">
      ${
        this.error
          ? html`<sl-alert variant="danger" open>${this.error}</sl-alert>`
          : nothing
      }
      ${this.renderStatus()}
      <div class="section">
        <div class="section-header">
          <h3>${terms.userGroupDomain.plural}</h3>
        </div>
        ${this.renderCurrentOrganizationTypes()}
      </div>
      <sl-divider></sl-divider>
      ${this.renderRegisterSection()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'member-workspace-abilities': MemberWorkspaceAbilities;
  }
}
